import React, { useState } from 'react';
import {
  Box, Heading, Text, Button, VStack, HStack, SimpleGrid,
  FormControl, FormLabel, Input, Select, Textarea, useToast,
  Card, CardBody, Alert, AlertIcon, Divider, Icon,
} from '@chakra-ui/react';
import { FaSyringe, FaCheck } from 'react-icons/fa';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';

export default function FacilityNewDraw() {
  const { facility, facilityId } = useOutletContext();
  const navigate = useNavigate();
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    patient_name: '', patient_email: '', patient_dob: '', patient_gender: '',
    patient_address: '', patient_phone: '',
    doctor_name: '', doctor_phone: '', doctor_email: '', doctor_address: '',
    special_instructions: '',
    is_self_pay: false,
    insurance_company: '', insurance_policy_number: '',
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async () => {
    if (!form.patient_name.trim()) { toast({ title: 'Patient name is required', status: 'warning', duration: 3000 }); return; }
    if (!facilityId) { toast({ title: 'Facility not found', description: 'Please log in again.', status: 'error', duration: 3000 }); return; }

    setSubmitting(true);
    try {
      // Generate unique draw code: timestamp base36 + random chars
      const drawCode = Date.now().toString(36).slice(-4).toUpperCase() + Math.random().toString(36).slice(-2).toUpperCase();

      const { error } = await supabase.from('submissions').insert({
        patient_name: form.patient_name.trim(),
        patient_email: form.patient_email.trim() || null,
        patient_dob: form.patient_dob || null,
        patient_gender: form.patient_gender || null,
        patient_address: form.patient_address.trim() || null,
        doctor_name: form.doctor_name.trim() || null,
        doctor_phone: form.doctor_phone.trim() || null,
        doctor_email: form.doctor_email.trim() || null,
        doctor_address: form.doctor_address.trim() || null,
        special_instructions: form.special_instructions.trim() || null,
        is_self_pay: form.is_self_pay,
        insurance_company: form.is_self_pay ? null : form.insurance_company.trim() || null,
        insurance_policy_number: form.is_self_pay ? null : form.insurance_policy_number.trim() || null,
        facility_id: facilityId,
        client_name: facility?.name || null,
        draw_code: drawCode,
        status: 'pending',
        submitted_at: new Date().toISOString(),
      });
      if (error) throw error;

      toast({ title: 'Blood draw submitted!', description: `Draw code: ${drawCode}`, status: 'success', duration: 5000 });
      setTimeout(() => navigate(`/facility/${facilityId}`), 1500);
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <Box>
      <Heading size="lg" fontWeight="700" mb={1}>New Blood Draw</Heading>
      <Text color="gray.500" fontSize="sm" mb={6}>Submit a blood draw order for a patient at {facility?.name}</Text>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {/* Patient Info */}
        <Card bg="white" shadow="sm">
          <CardBody>
            <Heading size="sm" color="teal.600" mb={4}>Patient Information</Heading>
            <VStack spacing={3}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Patient Name</FormLabel>
                <Input name="patient_name" value={form.patient_name} onChange={handleChange} placeholder="Last, First" />
              </FormControl>
              <HStack spacing={3} w="full">
                <FormControl>
                  <FormLabel fontSize="sm">Date of Birth</FormLabel>
                  <Input name="patient_dob" type="date" value={form.patient_dob} onChange={handleChange} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Gender</FormLabel>
                  <Select name="patient_gender" value={form.patient_gender} onChange={handleChange} placeholder="Select">
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </Select>
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel fontSize="sm">Email</FormLabel>
                <Input name="patient_email" type="email" value={form.patient_email} onChange={handleChange} placeholder="patient@email.com" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Phone</FormLabel>
                <Input name="patient_phone" value={form.patient_phone} onChange={handleChange} placeholder="(555) 123-4567" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Address</FormLabel>
                <Input name="patient_address" value={form.patient_address} onChange={handleChange} placeholder="123 Main St, City, State ZIP" />
              </FormControl>
            </VStack>
          </CardBody>
        </Card>

        {/* Doctor + Insurance */}
        <VStack spacing={6}>
          <Card bg="white" shadow="sm" w="full">
            <CardBody>
              <Heading size="sm" color="teal.600" mb={4}>Doctor Information</Heading>
              <VStack spacing={3}>
                <FormControl>
                  <FormLabel fontSize="sm">Doctor Name</FormLabel>
                  <Input name="doctor_name" value={form.doctor_name} onChange={handleChange} placeholder="Dr. Smith" />
                </FormControl>
                <HStack spacing={3} w="full">
                  <FormControl>
                    <FormLabel fontSize="sm">Phone</FormLabel>
                    <Input name="doctor_phone" value={form.doctor_phone} onChange={handleChange} placeholder="(555) 000-0000" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Email</FormLabel>
                    <Input name="doctor_email" value={form.doctor_email} onChange={handleChange} placeholder="doctor@clinic.com" />
                  </FormControl>
                </HStack>
              </VStack>
            </CardBody>
          </Card>

          <Card bg="white" shadow="sm" w="full">
            <CardBody>
              <Heading size="sm" color="teal.600" mb={4}>Insurance / Payment</Heading>
              <VStack spacing={3}>
                <HStack spacing={4} w="full">
                  <Button size="sm" variant={!form.is_self_pay ? 'solid' : 'outline'} colorScheme="blue" onClick={() => setForm(prev => ({ ...prev, is_self_pay: false }))}>Insurance</Button>
                  <Button size="sm" variant={form.is_self_pay ? 'solid' : 'outline'} colorScheme="green" onClick={() => setForm(prev => ({ ...prev, is_self_pay: true }))}>Self Pay</Button>
                </HStack>
                {!form.is_self_pay ? (
                  <>
                    <FormControl>
                      <FormLabel fontSize="sm">Insurance Company</FormLabel>
                      <Input name="insurance_company" value={form.insurance_company} onChange={handleChange} placeholder="Blue Cross Blue Shield" />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Policy Number</FormLabel>
                      <Input name="insurance_policy_number" value={form.insurance_policy_number} onChange={handleChange} placeholder="ABC123456" />
                    </FormControl>
                  </>
                ) : (
                  <Alert status="info" borderRadius="md" fontSize="sm"><AlertIcon />Patient will pay out of pocket. A payment link will be generated.</Alert>
                )}
              </VStack>
            </CardBody>
          </Card>

          <Card bg="white" shadow="sm" w="full">
            <CardBody>
              <FormControl>
                <FormLabel fontSize="sm">Special Instructions</FormLabel>
                <Textarea name="special_instructions" value={form.special_instructions} onChange={handleChange} placeholder="Fasting required, specific tests, etc." rows={3} />
              </FormControl>
            </CardBody>
          </Card>
        </VStack>
      </SimpleGrid>

      <Box mt={6}>
        <Button colorScheme="teal" size="lg" w="full" maxW="400px" leftIcon={<FaSyringe />}
          onClick={handleSubmit} isLoading={submitting} loadingText="Submitting..."
          fontWeight="700" py={7} borderRadius="xl">
          Submit Blood Draw
        </Button>
      </Box>
    </Box>
  );
}
